import { useEffect, useMemo, useState } from 'react'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { AssistantPanel, type BuddyEngine } from './assistant-panel'
import { getBuddyFlowConfig } from './buddy-flow/flow-config'
import { useBuddyFlowChat } from './buddy-flow/use-buddy-flow-chat'

// A failed flag fetch is not retried and leaves the flags at their defaults, so a held
// prompt is released after this long rather than waiting for a flip that never comes.
export const FLAG_HOLD_MS = 5000

interface BuddyEngineInput {
  flowFlagOn: boolean
  hasFlowApp: boolean
  flagsLoading: boolean
  holdExpired: boolean
}

// Buddy runs on the PHLO chat flow when the flag is on and this region has a Buddy chat app,
// and on aiassist otherwise. Until the flags load, a queued "Ask Buddy" prompt waits: sent
// on aiassist it would be lost when the panel remounts on the flow.
export const resolveBuddyEngine = ({
  flowFlagOn,
  hasFlowApp,
  flagsLoading,
  holdExpired,
}: BuddyEngineInput): { engine: BuddyEngine; holdPendingPrompt: boolean } =>
  flowFlagOn && hasFlowApp
    ? { engine: 'flow', holdPendingPrompt: false }
    : { engine: 'aiassist', holdPendingPrompt: hasFlowApp && flagsLoading && !holdExpired }

export const BuddyAssistant = () => {
  const { buddyFlowChatEnabled, isInitialData } = useFeatureFlags()
  const hasFlowApp = useMemo(() => getBuddyFlowConfig() !== null, [])
  const [holdExpired, setHoldExpired] = useState(false)

  useEffect(() => {
    if (!isInitialData) return
    const timer = setTimeout(() => setHoldExpired(true), FLAG_HOLD_MS)
    return () => clearTimeout(timer)
  }, [isInitialData])

  const { engine, holdPendingPrompt } = resolveBuddyEngine({
    flowFlagOn: buddyFlowChatEnabled,
    hasFlowApp,
    flagsLoading: !!isInitialData,
    holdExpired,
  })
  // The key remounts the panel when the flags resolve, so it never switches transport
  // mid-conversation.
  return engine === 'flow' ? (
    <AssistantPanel key="flow" engine="flow" useChat={useBuddyFlowChat} />
  ) : (
    <AssistantPanel key="aiassist" holdPendingPrompt={holdPendingPrompt} />
  )
}
